// Utility for authenticated API calls using localStorage token
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const getAuthToken = () => {
  return localStorage.getItem('advisor_token');
};

export const setAuthToken = (token) => {
  localStorage.setItem('advisor_token', token);
};

export const removeAuthToken = () => {
  localStorage.removeItem('advisor_token');
  localStorage.removeItem('user_role');
};

export const getUserRole = () => {
  return localStorage.getItem('user_role') || 'advisor';
};

export const setUserRole = (role) => {
  localStorage.setItem('user_role', role);
};

export const isAdvisor = () => {
  return getUserRole() === 'advisor';
};

export const isEmployee = () => {
  return getUserRole() === 'employee';
};

export const isAuthenticated = () => {
  return !!getAuthToken();
};

// XMLHttpRequest wrapper to avoid postMessage issues with fetch
export const authRequest = (method, url, data = null) => {
  return new Promise((resolve, reject) => {
    const token = getAuthToken();
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    
    const xhr = new XMLHttpRequest();
    xhr.open(method, fullUrl, true);
    
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    
    if (data) {
      xhr.setRequestHeader('Content-Type', 'application/json');
    }
    
    xhr.onload = function() {
      if (xhr.status === 401) {
        removeAuthToken();
        window.location.href = '/advisor/login';
        reject(new Error('Session expired'));
        return;
      }
      
      try {
        const response = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, status: xhr.status, data: response });
        } else {
          resolve({ ok: false, status: xhr.status, data: response });
        }
      } catch {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, status: xhr.status, data: xhr.responseText });
        } else {
          resolve({ ok: false, status: xhr.status, data: xhr.responseText });
        }
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(data ? JSON.stringify(data) : null);
  });
};

// Convenience methods
export const authGet = (url) => authRequest('GET', url);
export const authPost = (url, data) => authRequest('POST', url, data);
export const authPut = (url, data) => authRequest('PUT', url, data);
export const authPatch = (url, data) => authRequest('PATCH', url, data);
export const authDelete = (url) => authRequest('DELETE', url);

// Check if user is authenticated
export const checkAuth = async () => {
  const token = getAuthToken();
  if (!token) {
    return null;
  }
  
  try {
    const result = await authGet('/api/auth/me');
    if (result.ok) {
      return result.data;
    }
    removeAuthToken();
    return null;
  } catch {
    return null;
  }
};

// Logout function
export const logout = () => {
  removeAuthToken();
  window.location.href = '/advisor/login';
};

// File upload with auth
export const authUpload = (url, formData) => {
  return new Promise((resolve, reject) => {
    const token = getAuthToken();
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', fullUrl, true);
    
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    
    xhr.onload = function() {
      if (xhr.status === 401) {
        removeAuthToken();
        window.location.href = '/advisor/login';
        reject(new Error('Session expired'));
        return;
      }
      
      try {
        const response = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, status: xhr.status, data: response });
        } else {
          resolve({ ok: false, status: xhr.status, data: response });
        }
      } catch {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true, status: xhr.status, data: xhr.responseText });
        } else {
          resolve({ ok: false, status: xhr.status, data: xhr.responseText });
        }
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(formData);
  });
};
